import { FLAGS } from '@iheartjane/feature-flags';
import { Flex } from '@iheartjane/reefer';
import { registerJdid as registerJdidToRum } from '@iheartjane/tracking';
import { useEffect, useMemo } from 'react';
import { useLoaderData } from 'react-router';

import { BloomWizardModal } from '@jane/bloom/components/bloomWizardModal';
import { CommunicationBanner } from '@jane/bloom/components/communicationBanner';
import { Carousel } from '@jane/bloom/components/communicationBanner/carouselBanner/carousel';
import {
  FilterSelector,
  MobileFiltersModal,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { CategoryTab } from '@jane/bloom/components/routes/category/components/categoryTab';
import { SeoHeading } from '@jane/bloom/components/seoHeading';
import { BloomWizardBanner } from '@jane/bloom/components/wizardExposure/bloomWizardBanner';
import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useMenuTopRowWithServerData } from '@jane/bloom/hooks/dm/useMenuTopRowWithServerData';
import { BloomFilterProvider } from '@jane/bloom/hooks/filters';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { FeaturedResults } from '@jane/bloom/loaders/routes/featured.server';
import { createMetaVariables } from '@jane/bloom/resources/meta/createMetaVariables';
import { DEFAULT_TITLE } from '@jane/bloom/resources/meta/routes/featured';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import { registerJdid } from '@jane/bloom/services/tracking/mixpanel';
import { trackSearchedMenuProducts } from '@jane/bloom/services/tracking/track';

import { FeaturedTab } from './components/featuredTab';

export function FeaturedRouteComponent() {
  const { cart, customSEO, featureFlags, metaProps, pinnedStore } =
    useRootLoaderData();
  const loaderData = useLoaderData<FeaturedResults>();

  useEffect(() => {
    registerJdid(loaderData.jdid);
    registerJdidToRum(loaderData.jdid);
  }, [loaderData.jdid]);

  const rowInstance = useMemoizedRowInstance(
    loaderData.viewMode === 'category'
      ? loaderData.recommendedSortResults
      : undefined
  );

  // Handles server-side initial load and client-side refetching of the menu top row
  const {
    pauseRefetching,
    rowInstance: menuTopRowInstance,
    unpauseRefetching,
  } = useMenuTopRowWithServerData({
    initialMenuTopRow: loaderData.menuTopRow,
    searchFilter: loaderData.searchFilterWithoutBrands,
    storeId: loaderData.storeId,
  });

  const totalProducts: number = useMemo(() => {
    if (rowInstance?.totalProducts) return rowInstance.totalProducts;
    if (
      loaderData.viewMode === 'featured' &&
      loaderData['customRowMenuProducts']
    ) {
      return Object.values(loaderData['customRowMenuProducts']).reduce(
        (acc, curr) => acc + curr.totalCount,
        0
      );
    }
    return 0;
  }, [rowInstance?.totalProducts, loaderData]);

  useEffect(() => {
    trackSearchedMenuProducts({
      hits: totalProducts ?? 0,
      searchCriteria: loaderData.searchCriteria,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(loaderData.searchCriteria), totalProducts]);

  return (
    <BloomFilterProvider
      availableFilters={loaderData.availableFilters}
      searchCriteria={loaderData.searchCriteria}
    >
      <SeoHeading
        customSEOPage={customSEO?.featured ?? {}}
        defaultTitle={DEFAULT_TITLE}
        metaVariables={createMetaVariables(metaProps)}
      />

      {loaderData.showCarousel &&
        loaderData.desktopCarouselBanner.length > 0 && (
          <div className="desktop-carousel-banner">
            <Carousel
              items={loaderData.desktopCarouselBanner}
              storeName={pinnedStore?.name}
            />
          </div>
        )}

      {loaderData.showCarousel &&
        loaderData.mobileCarouselBanner.length > 0 && (
          <div className="mobile-carousel-banner">
            <Carousel
              items={loaderData.mobileCarouselBanner}
              storeName={pinnedStore?.name}
            />
          </div>
        )}

      <CommunicationBanner
        communicationBanner={loaderData.communicationBanner}
        showImageBanner={loaderData.showImageBanner}
      />

      <div className="featured-filters-container">
        <FilterSelector
          availableProductsCount={rowInstance?.totalProducts}
          sortOptions={loaderData.sort.sortOptions}
          currentSort={loaderData.sort.currentSort}
          shouldShowGold={loaderData.janeGold.shouldShowGold}
          hasJaneGoldProducts={loaderData.janeGold.hasJaneGoldProducts}
        />
      </div>

      <Flex
        alignItems="center"
        justifyContent="flex-start"
        className="filterContainer"
      >
        <MobileFiltersModal
          shouldShowGold={loaderData.janeGold.shouldShowGold}
          availableProductsCount={rowInstance?.totalProducts}
          sortOptions={loaderData.sort.sortOptions}
          currentSort={loaderData.sort.currentSort}
          hasJaneGoldProducts={loaderData.janeGold.hasJaneGoldProducts}
        />
      </Flex>

      <BloomWizardModal
        brandSpecials={loaderData.brandSpecials}
        specials={loaderData.enabledSpecialsForRow}
      >
        {({ openModal }) =>
          featureFlags[FLAGS.obWizardBannerEnabled] ? (
            <div className="wizard-banner-container">
              <BloomWizardBanner
                onClickStart={() => openModal(WIZARD_SOURCE.BANNER)}
              />
            </div>
          ) : null
        }
      </BloomWizardModal>

      {loaderData.viewMode === 'category' && (
        <CategoryTab
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={loaderData.brandSpecials}
          cart={cart}
          category={loaderData.category}
          currentSort={loaderData.sort.currentSort}
          customCategoryLabels={loaderData.customCategoryLabels}
          customLanguage={loaderData.customLanguage}
          featuredTab
          menuTopRowInstance={menuTopRowInstance}
          preloadedProductIds={loaderData.menuProductImgsAboveTheFold.map(
            ({ id }) => id
          )}
          rowInstance={rowInstance}
          sortOptions={loaderData.sort.sortOptions}
          specials={loaderData.enabledSpecialsForRow}
          janeGold={loaderData.janeGold}
          pinnedStore={pinnedStore}
          enabledSpecialsForRow={loaderData.enabledSpecialsForRow}
        />
      )}
      {loaderData.viewMode === 'featured' && (
        <FeaturedTab
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={loaderData.brandSpecials}
          cart={cart}
          complianceLanguage={loaderData.complianceLanguage}
          customLanguage={loaderData.customLanguage}
          featuredPlacements={loaderData.featuredPlacements}
          enabledSpecialsForRow={loaderData.enabledSpecialsForRow}
          menuTopRowInstance={menuTopRowInstance}
          janeGold={loaderData.janeGold}
          pinnedStore={pinnedStore}
          userHasLinkedBank={!!loaderData.aeropayUser?.bank_accounts?.length}
        />
      )}
    </BloomFilterProvider>
  );
}
